import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { 
  Wrench, 
  Database, 
  RefreshCw, 
  Download, 
  Upload,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Play,
  Pause,
  Settings,
  Activity,
  HardDrive,
  Wifi,
  Zap
} from 'lucide-react';

const SystemMaintenance = () => {
  const [activeTab, setActiveTab] = useState('status');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isBackupRunning, setIsBackupRunning] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);

  const systemServices = [
    { name: 'API Server', status: 'running', uptime: '15 days', cpu: '12%', memory: '45%' },
    { name: 'Database', status: 'running', uptime: '15 days', cpu: '8%', memory: '32%' },
    { name: 'File Storage', status: 'running', uptime: '15 days', cpu: '3%', memory: '18%' },
    { name: 'Email Service', status: 'warning', uptime: '2 hours', cpu: '5%', memory: '12%' },
    { name: 'Backup Service', status: 'running', uptime: '15 days', cpu: '2%', memory: '8%' }
  ];

  const backupHistory = [
    { id: 1, date: '2024-01-15 02:00', type: 'Full', size: '2.4 GB', status: 'completed', duration: '12 min' },
    { id: 2, date: '2024-01-14 02:00', type: 'Full', size: '2.3 GB', status: 'completed', duration: '11 min' },
    { id: 3, date: '2024-01-13 02:00', type: 'Full', size: '2.2 GB', status: 'completed', duration: '10 min' },
    { id: 4, date: '2024-01-12 02:00', type: 'Full', size: '2.1 GB', status: 'failed', duration: '8 min' },
    { id: 5, date: '2024-01-11 02:00', type: 'Full', size: '2.0 GB', status: 'completed', duration: '9 min' }
  ];

  const maintenanceTasks = [
    { id: 1, name: 'Database Optimization', lastRun: '2024-01-10', nextRun: '2024-01-17', frequency: 'Weekly' },
    { id: 2, name: 'Log Cleanup', lastRun: '2024-01-15', nextRun: '2024-01-16', frequency: 'Daily' },
    { id: 3, name: 'Security Scan', lastRun: '2024-01-12', nextRun: '2024-01-19', frequency: 'Weekly' },
    { id: 4, name: 'Storage Cleanup', lastRun: '2024-01-08', nextRun: '2024-01-22', frequency: 'Bi-weekly' },
    { id: 5, name: 'Performance Analysis', lastRun: '2024-01-01', nextRun: '2024-02-01', frequency: 'Monthly' }
  ];

  const handleBackup = async () => {
    setIsBackupRunning(true);
    setBackupProgress(0);
    
    // Simulate backup progress
    const interval = setInterval(() => {
      setBackupProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsBackupRunning(false);
          toast.success('Backup completed successfully');
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  const handleMaintenanceMode = () => {
    setMaintenanceMode(!maintenanceMode);
    toast.success(`Maintenance mode ${!maintenanceMode ? 'enabled' : 'disabled'}`);
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      running: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      stopped: 'bg-gray-100 text-gray-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };

    const icons = {
      running: CheckCircle,
      warning: AlertTriangle,
      error: AlertTriangle,
      stopped: Pause,
      completed: CheckCircle,
      failed: AlertTriangle
    };

    const Icon = icons[status] || CheckCircle;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const TabButton = ({ id, label, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 font-medium rounded-lg transition-colors ${
        active
          ? 'bg-[#DCEAF5] text-[#207CE5] border border-[#207CE5]'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Maintenance</h1>
          <p className="text-gray-600">Monitor system health and perform maintenance tasks</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleMaintenanceMode}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              maintenanceMode 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-yellow-600 text-white hover:bg-yellow-700'
            }`}
          >
            <AlertTriangle className="h-4 w-4" />
            <span>{maintenanceMode ? 'Disable' : 'Enable'} Maintenance Mode</span>
          </button>
        </div>
      </div>

      {/* Maintenance Mode Alert */}
      {maintenanceMode && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Maintenance Mode Active</h3>
              <p className="text-sm text-red-700">The system is currently in maintenance mode. Users cannot access the application.</p>
            </div>
          </div>
        </div>
      )}

      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <Activity className="h-6 w-6 text-[#008080]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Uptime</p>
              <p className="text-2xl font-bold text-gray-900">15 days</p>
              <p className="text-xs text-green-600">99.9% availability</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <HardDrive className="h-6 w-6 text-[#207CE5]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="text-2xl font-bold text-gray-900">78%</p>
              <p className="text-xs text-blue-600">2.4 TB / 3.1 TB</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <Zap className="h-6 w-6 text-[#00BCD4]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">CPU Usage</p>
              <p className="text-2xl font-bold text-gray-900">24%</p>
              <p className="text-xs text-purple-600">Average load</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <Wifi className="h-6 w-6 text-[#4682B4]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Network I/O</p>
              <p className="text-2xl font-bold text-gray-900">1.2 GB</p>
              <p className="text-xs text-orange-600">Last 24 hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <TabButton id="status" label="System Status" active={activeTab === 'status'} onClick={setActiveTab} />
        <TabButton id="backup" label="Backup & Restore" active={activeTab === 'backup'} onClick={setActiveTab} />
        <TabButton id="tasks" label="Maintenance Tasks" active={activeTab === 'tasks'} onClick={setActiveTab} />
      </div>

      {/* Tab Content */}
      {activeTab === 'status' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">System Services Status</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uptime
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CPU Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Memory
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {systemServices.map((service, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Settings className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-sm font-medium text-gray-900">{service.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={service.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {service.uptime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {service.cpu}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {service.memory}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-[#207CE5] hover:text-[#0B1C2C]" title="Restart">
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900" title="Stop">
                          <Pause className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'backup' && (
        <div className="space-y-6">
          {/* Backup Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Backup Operations</h2>
              <div className="flex items-center space-x-3">
                <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Upload className="h-4 w-4" />
                  <span>Restore</span>
                </button>
                <button
                  onClick={handleBackup}
                  disabled={isBackupRunning}
                  className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] disabled:opacity-50"
                >
                  <Database className="h-4 w-4" />
                  <span>{isBackupRunning ? 'Running...' : 'Start Backup'}</span>
                </button>
              </div>
            </div>

            {isBackupRunning && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Backup Progress</span>
                  <span className="text-sm text-gray-500">{backupProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#207CE5] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${backupProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Backup History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Backup History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backupHistory.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.size}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.duration}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={backup.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button className="text-[#207CE5] hover:text-[#0B1C2C]" title="Download">
                            <Download className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Scheduled Maintenance Tasks</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {maintenanceTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-[#DCEAF5] rounded-lg">
                      <Wrench className="h-5 w-5 text-[#207CE5]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{task.name}</h4>
                      <p className="text-sm text-gray-500">
                        Last run: {task.lastRun} • Next: {task.nextRun} • {task.frequency}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-400 hover:text-[#207CE5]" title="Run Now">
                      <Play className="h-4 w-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600" title="Configure">
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemMaintenance;